import type { LatLng } from '~/_shared/types/latLng';
import {
  isMarkerStyle, type MarkerEntityStyle,
  MarkerSegment,
} from '~/_shared/types/marker.types';
import { convertColorToWebGLColor } from '~/_shared/utils/colors/colors.helpers';
import type { PlaceZIndex } from '~/map/zIndexes/usePlacesSearchZIndexes.hook';
import type { PlaceId } from '~/places/places.types';
import type { PlacesSearchId } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { mapFont } from '../mapLabelFonts';
import { createWebGLMarker } from '../markers/manager/mapMarkerManager';
import type { ExtendsWebglLayers } from '../webgl/useWebGL';

export const PLACES_RADIUS_CIRCLE = 8;

export type PlaceItemRenderData = Readonly<{
  id: PlaceId;
  marker: MarkerEntityStyle;
  backgroundColor: string;
  fontColor: string;
  numberLabel: string;
  latLng: LatLng;
  zIndex: PlaceZIndex;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}>;

type RenderedItemHandlers = Readonly<{
  mouseOverHandler: (() => void) | null;
  mouseOutHandler: (() => void) | null;
  clickHandler: (() => void) | null;
}>;

type RenderedItemData = RenderedItemHandlers & Readonly<{
  marker: WebglOverlayMarker | null;
  circle: WebglOverlayCircleArea;
  label: WebglOverlayLabel;
}>;

type ItemsMap = Map<PlacesSearchId, Map<PlaceId, RenderedItemData>>;

type RequiredLayers = ExtendsWebglLayers<Readonly<{
  MapObjectMarkers: MarkersLayer;
  MapObjectCirles: CircleAreaLayer;
  MapObjectLabels: LabelsLayer;
}>>;

export class MapPlacesManager {
  private readonly overlayLayers: RequiredLayers;
  private renderedItems: ItemsMap = new Map();

  constructor(overlayLayers: RequiredLayers) {
    this.overlayLayers = overlayLayers;
  }

  public addPlacesSearch({
    id,
    items,
  }: {
    id: PlacesSearchId;
    items: ReadonlyArray<PlaceItemRenderData>;
  }) {
    if (this.renderedItems.has(id)) {
      this.removePlacesSearch(id);
    }

    const placesMarkerMap: Map<PlaceId, RenderedItemData> = new Map();

    items.forEach((item) => {
      const renderedItem = this.addPlace(item);
      placesMarkerMap.set(
        item.id,
        renderedItem,
      );
    });

    this.renderedItems.set(id, placesMarkerMap);
  }

  public removePlacesSearch(id: PlacesSearchId) {
    const renderedItems = this.renderedItems.get(id);
    if (renderedItems) {
      renderedItems.forEach((item) => {

        if (item.marker) {
          this.removeEventListenersFromMapObject(item.marker, item);
          this.overlayLayers.MapObjectMarkers.remove(item.marker);
        }

        this.removeEventListenersFromMapObject(item.circle, item);
        this.overlayLayers.MapObjectCirles.remove(item.circle);

        this.overlayLayers.MapObjectLabels.remove(item.label);
      });
      this.renderedItems.delete(id);
    }
  }

  private addPlace({
    marker: markerEntityStyle,
    latLng,
    zIndex,
    backgroundColor,
    fontColor,
    numberLabel,
    onClick,
    onMouseEnter,
    onMouseLeave,
  }: PlaceItemRenderData) {
    const backgroundWebGLColor = convertColorToWebGLColor(backgroundColor);

    const circle = new WebGLOverlay.CircleArea({
      radius: PLACES_RADIUS_CIRCLE,
      lat: latLng.lat,
      lng: latLng.lng,
      units: 'pixels',
      staticSize: true,
      fillColor: backgroundWebGLColor,
      borderWidth: 0,
      zIndex: zIndex.circle,
      interactive: onClick ? true : false,
    });

    this.overlayLayers.MapObjectCirles.add(circle);

    this.addEventListenersToMapObject(circle, { onClick, onMouseEnter, onMouseLeave });

    const label = new WebGLOverlay.Label({
      zIndex: zIndex.text,
      lat: latLng.lat,
      lng: latLng.lng,
      text: {
        value: numberLabel,
        fontSize: 8,
        fillColor: convertColorToWebGLColor(fontColor),
        fillWidth: 0.58,
        strokeWidth: 0.75,
        font: mapFont.arial.name,
      },
      verticalAnchor: 'center',
      horizontalAnchor: 'center',
      opacity: 1,
      staticSize: true,
      interactive: false,
    });

    this.overlayLayers.MapObjectLabels.add(label);

    let marker = null;

    if (isMarkerStyle(markerEntityStyle)) {
      marker = createWebGLMarker({
        latLngData: latLng,
        zIndex: zIndex.marker,
        style: markerEntityStyle,
        color: { color: backgroundWebGLColor, opacity: 1, backgroundOpacity: 1 },
        segment: MarkerSegment.MAIN,
        isHidden: false,
        size: markerEntityStyle.size,
        offset: { x: 0, y: -PLACES_RADIUS_CIRCLE + 1 },
      });
      this.overlayLayers.MapObjectMarkers.add(marker);

      this.addEventListenersToMapObject(marker, { onClick, onMouseEnter, onMouseLeave });
    }

    return {
      mouseOverHandler: onMouseEnter ?? null,
      mouseOutHandler: onMouseLeave ?? null,
      clickHandler: onClick ?? null,
      marker,
      circle,
      label,
    };
  }

  private addEventListenersToMapObject(
    object: WebglOverlayMarker | WebglOverlayCircleArea | null,
    handlers: {
      onClick?: () => void;
      onMouseEnter?: () => void;
      onMouseLeave?: () => void;
    }
  ) {
    if (object) {
      if (handlers.onClick) {
        object.addEventListener('click', handlers.onClick);
      }
      if (handlers.onMouseEnter) {
        object.addEventListener('mouseover', handlers.onMouseEnter);
      }
      if (handlers.onMouseLeave) {
        object.addEventListener('mouseout', handlers.onMouseLeave);
      }
    }
  }

  private removeEventListenersFromMapObject(
    object: WebglOverlayMarker | WebglOverlayCircleArea | null,
    handlers: RenderedItemHandlers
  ) {
    if (object) {
      if (handlers.clickHandler) {
        object.removeEventListener('click', handlers.clickHandler);
      }
      if (handlers.mouseOverHandler) {
        object.removeEventListener('mouseover', handlers.mouseOverHandler);
      }
      if (handlers.mouseOutHandler) {
        object.removeEventListener('mouseout', handlers.mouseOutHandler);
      }
    }
  }
}
